import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EncryptDecryptService } from '../../Services/encrypt-decrypt.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { getStoredLang } from '../constants/supported-languages';

/** Strips Bearer auth and injects X-API-Key for the Python Skey-AI service.
 *  FormData body is preserved as-is — must not be encrypted. */
function applySkeyAiAuth(req: HttpRequest<any>): HttpRequest<any> {
  const apiKey: string = (environment as any).SKEY_AI_API_KEY || '';
  let hdrs: HttpHeaders = req.headers.delete('Authorization');
  if (apiKey && !apiKey.startsWith('__')) {
    hdrs = hdrs.set('X-API-Key', apiKey);
  }
  return req.clone({ headers: hdrs, body: req.body });
}

// Function-based HTTP Interceptor for Angular 18
export const authTokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Inject services inside the function using Angular's DI system
  const router = inject(Router);
  const encryptDecryptService = inject(EncryptDecryptService);

  // Local static assets (i18n JSON, icons, etc.) must pass through untouched —
  // fetch backend rejects GET/HEAD requests that have a body.
  if (request.url.startsWith('assets/') || request.url.startsWith('/assets/')) {
    return next(request);
  }

  const accessToken = localStorage.getItem('accessToken');
  const lang = getStoredLang();
  // Downloads that answer with the file's bytes: the body still goes out encrypted, the response is not decrypted.
  const isExportFile = request.url.includes('ExportDocumentData') || request.url.includes('SpendExplorer/exportfile');
  const isAWS = request.url.includes('DocumentExtractor');
  // Skey AI (Python) service — FormData body must not be encrypted; auth via X-API-Key header
  const isSkeyAI = request.url.startsWith(environment.SKEY_AI_URL);
  // Audit BFF (#9320/#9441) + tenant-tier endpoints return PLAIN JSON — they are NOT part of the
  // encrypted-payload protocol (the .NET EncryptResponseAttribute skips actions with no `input`
  // param). They must bypass body encryption AND response decryption, or decryptUsingAES256 +
  // JSON.parse chokes on the cleartext and the call surfaces as a failure.
  // HtmlPages returns an uploaded HTML document as text/html, and its error envelopes are
  // exempted server-side to match, so there is nothing here to decrypt or JSON.parse.
  const isPlainJson = ['/api/audit', '/api/TenantTier', '/api/SavingsRecommendations', '/api/integritynext', '/api/HtmlPages']
    .some(path => request.url.includes(path));

  let clonedRequest = request.clone();

  // If access token is present, set headers and optionally encrypt/decrypt the body
  if (accessToken) {
    clonedRequest = clonedRequest.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
        'Accept-Language': lang,
      },
    });

    if (isExportFile) {
      clonedRequest = clonedRequest.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        body: { Payload: encryptDecryptService.encryptUsingAES256(JSON.stringify(request.body)) },
      });
      return next(clonedRequest);
    } else if (isAWS) {
      clonedRequest = clonedRequest.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        body: request.body,
      });
      return next(clonedRequest);
    } else if (isSkeyAI) {
      return next(applySkeyAiAuth(request));
    } else if (isPlainJson) {
      // Plain-JSON endpoints: keep the Bearer header, send the body unencrypted, and do NOT
      // decrypt the response (it is already cleartext JSON).
      clonedRequest = clonedRequest.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        body: request.body,
      });
      return next(clonedRequest);
    } else {
      clonedRequest = clonedRequest.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        body: { Payload: encryptDecryptService.encryptUsingAES256(JSON.stringify(request.body)) },
      });
      return next(clonedRequest).pipe(
        mergeMap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            const decryptedBody = encryptDecryptService.decryptUsingAES256(event.body);
            return of(event.clone({ body: JSON.parse(decryptedBody) }));
          }
          return of(event);
        }),
        catchError((error) => handleError(error, router))
      );
    }

  } else {
    if (isSkeyAI) {
      return next(applySkeyAiAuth(request));
    }

    // If no access token, encrypt the request body and still pass the locale
    clonedRequest = clonedRequest.clone({
      setHeaders: { 'Accept-Language': lang },
      body: { Payload: encryptDecryptService.encryptUsingAES256(JSON.stringify(request.body)) },
    });

    return next(clonedRequest).pipe(
      mergeMap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const decryptedBody = encryptDecryptService.decryptUsingAES256(event.body);
          return of(event.clone({ body: JSON.parse(decryptedBody) }));
        }
        return of(event); // Return non-response events as-is
      }),
      catchError((error) => handleError(error, router))
    );
  }
};

// Error handling function
function handleError(error: any, router: Router): Observable<any> {
  if (error.status === 401 || error.status === 403) {
    localStorage.clear();
    router.navigate(['login']);
  }
  return throwError(error);
}
